export interface AnnotationRange {
  readonly id: string;
  readonly start: number;
  readonly end: number;
}

export type AnnotationRepairReason =
  | "integer-normalized"
  | "nan-normalized"
  | "reversed"
  | "clamped"
  | "empty-range"
  | "overlap-truncated"
  | "empty-after-overlap"
  | "duplicate-id";

export interface AnnotationRepair {
  readonly order: number;
  readonly original: AnnotationRange;
  readonly repaired: AnnotationRange | null;
  readonly outcome: "repaired" | "dropped";
  readonly reasons: readonly AnnotationRepairReason[];
}

export interface AnnotationRepairResult {
  readonly accepted: readonly AnnotationRange[];
  readonly repairs: readonly AnnotationRepair[];
}

export interface AnnotationProjection {
  destroy(): void;
}

interface WorkingRange {
  readonly original: AnnotationRange;
  readonly inputOrder: number;
  readonly reasons: AnnotationRepairReason[];
  start: number;
  end: number;
}

interface ProcessedRange {
  readonly order: number;
  readonly working: WorkingRange;
  repaired: AnnotationRange | null;
}

interface ProjectionOwnership {
  readonly canonicalText: string;
  readonly originalNodes: readonly Node[];
  readonly projectedNodes: readonly Node[];
}

const projectionOwners = new WeakMap<HTMLElement, ProjectionOwnership>();

const frozenRange = (range: AnnotationRange): AnnotationRange => Object.freeze({
  id: range.id,
  start: range.start,
  end: range.end,
});

const normalizeInteger = (
  value: number,
  reasons: AnnotationRepairReason[],
): number => {
  if (Number.isNaN(value)) {
    if (!reasons.includes("nan-normalized")) reasons.push("nan-normalized");
    return 0;
  }

  const normalized = Math.trunc(value);
  if (normalized !== value && !reasons.includes("integer-normalized")) {
    reasons.push("integer-normalized");
  }
  return normalized;
};

const clamp = (value: number, maximum: number): number => Math.min(maximum, Math.max(0, value));

const compareIds = (left: string, right: string): number => {
  if (left < right) return -1;
  if (left > right) return 1;
  return 0;
};

const recordRepair = (processed: ProcessedRange): AnnotationRepair => Object.freeze({
  order: processed.order,
  original: processed.working.original,
  repaired: processed.repaired,
  outcome: processed.repaired === null ? "dropped" : "repaired",
  reasons: Object.freeze([...processed.working.reasons]),
});

/**
 * Repairs imported annotation offsets using Unicode code-point lengths.
 * Returned ranges and diagnostics are detached from and immutable to callers.
 */
export const repairAnnotationRanges = (
  canonicalText: string,
  ranges: readonly AnnotationRange[],
): AnnotationRepairResult => {
  const codePointLength = Array.from(canonicalText).length;
  const working = ranges.map((range, inputOrder): WorkingRange => {
    const original = frozenRange(range);
    const reasons: AnnotationRepairReason[] = [];
    let start = normalizeInteger(range.start, reasons);
    let end = normalizeInteger(range.end, reasons);

    if (start > end) {
      [start, end] = [end, start];
      reasons.push("reversed");
    }

    const clampedStart = clamp(start, codePointLength);
    const clampedEnd = clamp(end, codePointLength);
    if (clampedStart !== start || clampedEnd !== end) reasons.push("clamped");

    return {
      original,
      inputOrder,
      reasons,
      start: clampedStart,
      end: clampedEnd,
    };
  });

  working.sort((left, right) =>
    left.start - right.start
    || left.end - right.end
    || compareIds(left.original.id, right.original.id)
    || left.inputOrder - right.inputOrder
  );

  const pipelineAccepted: AnnotationRange[] = [];
  const processed = working.map((range, order): ProcessedRange => {
    if (range.start === range.end) {
      range.reasons.push("empty-range");
      return { order, working: range, repaired: null };
    }

    const previous = pipelineAccepted.at(-1);
    if (previous !== undefined && range.start < previous.end) {
      range.start = previous.end;
      range.reasons.push("overlap-truncated");
      if (range.start >= range.end) {
        range.reasons.push("empty-after-overlap");
        return { order, working: range, repaired: null };
      }
    }

    const repaired = frozenRange({
      id: range.original.id,
      start: range.start,
      end: range.end,
    });
    pipelineAccepted.push(repaired);
    return { order, working: range, repaired };
  });

  /*
   * Duplicate collision handling is deliberately layered after required repair
   * steps 1-7. Within each sorted ID group, the first range that survived those
   * steps is the candidate; later survivors are dropped. Every group member,
   * including already-dropped members and the candidate, receives evidence.
   */
  const duplicateGroups = new Map<string, ProcessedRange[]>();
  for (const entry of processed) {
    const group = duplicateGroups.get(entry.working.original.id) ?? [];
    group.push(entry);
    duplicateGroups.set(entry.working.original.id, group);
  }
  for (const group of duplicateGroups.values()) {
    if (group.length < 2) continue;
    let candidateSelected = false;
    for (const entry of group) {
      entry.working.reasons.push("duplicate-id");
      if (entry.repaired === null) continue;
      if (!candidateSelected) {
        candidateSelected = true;
      } else {
        entry.repaired = null;
      }
    }
  }

  const accepted = processed.flatMap((entry) => entry.repaired === null ? [] : [entry.repaired]);
  const repairs = processed
    .filter((entry) => entry.working.reasons.length > 0)
    .map(recordRepair);

  return Object.freeze({
    accepted: Object.freeze(accepted),
    repairs: Object.freeze(repairs),
  });
};

const developmentDiagnosticsEnabled = (): boolean => {
  const runtime = globalThis as typeof globalThis & {
    process?: { env?: { NODE_ENV?: string } };
  };
  return runtime.process?.env?.NODE_ENV !== "production";
};

const assertCanonicalText = (element: HTMLElement, canonicalText: string): void => {
  if (developmentDiagnosticsEnabled() && element.textContent !== canonicalText) {
    throw new Error("annotation projection changed canonical textContent");
  }
};

const assertProjectableRanges = (
  ranges: readonly AnnotationRange[],
  codePointLength: number,
): void => {
  let previousEnd = 0;
  const ids = new Set<string>();
  for (const range of ranges) {
    if (!Number.isInteger(range.start) || !Number.isInteger(range.end)
        || range.start < previousEnd || range.start < 0
        || range.start >= range.end || range.end > codePointLength
        || ids.has(range.id)) {
      throw new RangeError("annotation projection requires sorted, accepted code-point ranges");
    }
    previousEnd = range.end;
    ids.add(range.id);
  }
};

const projectedContentIsIntact = (
  element: HTMLElement,
  ownership: ProjectionOwnership,
): boolean => {
  const currentNodes = Array.from(element.childNodes);
  return element.textContent === ownership.canonicalText
    && currentNodes.length === ownership.projectedNodes.length
    && currentNodes.every((node, index) => node === ownership.projectedNodes[index]);
};

/**
 * Projects accepted ranges over an element's existing canonical text. The
 * caller remains the text owner; destroying an intact projection restores the
 * exact original child nodes. External DOM or text changes are never replaced.
 */
export const projectAnnotations = (
  element: HTMLElement,
  ranges: readonly AnnotationRange[],
): AnnotationProjection => {
  if (projectionOwners.has(element)) {
    throw new Error("annotation projection already owns this element");
  }

  const canonicalText = element.textContent;
  const codePoints = Array.from(canonicalText);
  assertProjectableRanges(ranges, codePoints.length);

  const originalNodes = Array.from(element.childNodes);
  const projectedNodes: Node[] = [];
  let cursor = 0;

  for (const range of ranges) {
    if (cursor < range.start) {
      projectedNodes.push(element.ownerDocument.createTextNode(
        codePoints.slice(cursor, range.start).join(""),
      ));
    }

    const mark = element.ownerDocument.createElement("mark");
    mark.setAttribute("data-annotation-id", range.id);
    mark.textContent = codePoints.slice(range.start, range.end).join("");
    projectedNodes.push(mark);
    cursor = range.end;
  }

  if (cursor < codePoints.length) {
    projectedNodes.push(element.ownerDocument.createTextNode(codePoints.slice(cursor).join("")));
  }

  const ownership: ProjectionOwnership = {
    canonicalText,
    originalNodes,
    projectedNodes,
  };
  projectionOwners.set(element, ownership);

  try {
    element.replaceChildren(...projectedNodes);
    assertCanonicalText(element, canonicalText);
  } catch (error) {
    try {
      element.replaceChildren(...originalNodes);
      assertCanonicalText(element, canonicalText);
    } finally {
      if (projectionOwners.get(element) === ownership) {
        projectionOwners.delete(element);
      }
    }
    throw error;
  }

  let destroyed = false;
  return Object.freeze({
    destroy(): void {
      if (destroyed) return;
      destroyed = true;

      if (projectionOwners.get(element) !== ownership) return;
      if (!projectedContentIsIntact(element, ownership)) {
        projectionOwners.delete(element);
        return;
      }

      try {
        element.replaceChildren(...originalNodes);
        assertCanonicalText(element, canonicalText);
      } finally {
        if (projectionOwners.get(element) === ownership) {
          projectionOwners.delete(element);
        }
      }
    },
  });
};
